import { Link } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import { PageShell } from '@/components/ui/site-chrome'
import { Accordion, Chip } from '@/components/lc'

/* ================================================================== */
/*  Contact: one honest lane (everything reaches the same inbox),      */
/*  the office beside it, safety notes as an accordion.                */
/* ================================================================== */

const EMAIL = 'info@lifecraft4u.com'
const WHATSAPP = '+91 8796790333'
const WHATSAPP_LINK = 'https://wa.me/918796790333'

const SAFETY = [
  { q: 'Official channels only', a: 'All official emails come from the @lifecraft4u.com domain. If a message claims to be us from anywhere else, it is not us. Please verify the sender before replying.' },
  { q: 'We never ask for credentials', a: 'No one from LifeCraft4U will ever ask for your passwords, OTPs or card details over email or chat. Ever.' },
  { q: 'Report suspicious activity', a: 'If you receive unusual requests claiming to be from us, forward them to our team immediately and we will investigate.' },
  { q: 'Substance over speed', a: 'We read everything. Response times are honest estimates, and every query is handled with the attention it deserves.' },
]

export default function Contact() {
  return (
    <PageShell>
      {/* hero */}
      <section className="px-5 pb-14 pt-36 md:pt-44">
        <div className="container-lc">
          <p className="label text-lc-mute">Get support</p>
          <h1 className="mt-4 max-w-3xl font-display text-5xl">
            Talk to a <span className="rounded-lg bg-lime px-3 text-night">human</span>.
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-lc-mute">
            Clarity, responsiveness, accountability. Whether you are a member, a LifeCrafter,
            a partner or just curious, one note reaches the right person.
          </p>
        </div>
      </section>

      {/* write to us + office */}
      <section className="px-5 pb-20">
        <div className="container-lc grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="lc-glass relative h-full overflow-hidden rounded-card p-5 xl:p-7 md:p-9">
              <span className="blob absolute -right-14 -top-14 h-48 w-48 opacity-30" style={{ background: '#7C3AED' }} aria-hidden="true" />
              <div className="relative">
                <p className="label text-lc-mute">One inbox, real people</p>
                <h2 className="mt-3 font-display text-3xl">Write to us.</h2>
                <p className="mt-4 max-w-lg text-[15px] xl:text-[16px] leading-relaxed text-lc-mute">
                  Membership, hosting, events, partnerships or privacy: it all lands in the same
                  place and gets routed to the right person. A line of context helps us help you faster:
                </p>
                <ul className="mt-4 max-w-lg space-y-2 text-[15px] leading-relaxed text-lc-mute">
                  <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" aria-hidden="true" /> what it's about, in a sentence</li>
                  <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" aria-hidden="true" /> your city, if it involves an in-person event</li>
                  <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" aria-hidden="true" /> your craft, if you are applying as a LifeCrafter</li>
                </ul>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <a
                    href={`mailto:${EMAIL}`}
                    className="rounded-pill bg-lime px-7 py-3.5 text-[15px] xl:text-[16px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
                  >
                    Email us · {EMAIL}
                  </a>
                  <a
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noreferrer"
                    className="lc-glass rounded-pill px-7 py-3.5 text-[15px] xl:text-[16px] font-bold text-lc-ink transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
                  >
                    Connect on WhatsApp · {WHATSAPP}
                  </a>
                  <span className="label !text-[11px] text-lc-mute">
                    Replies within <span className="text-lc-accent">24 to 48 hours</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* office + shortcuts */}
          <div className="flex flex-col gap-6 lg:col-span-4">
            <div className="lc-glass-soft rounded-card p-5 xl:p-7">
              <span className="lc-glass grid h-11 w-11 place-items-center rounded-tile text-lc-accent">
                <Icon name="mapPin" size={20} />
              </span>
              <h3 className="mt-5 font-display text-xl">Registered office</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-lc-mute">
                D-502, Brigade Gardenia,<br />
                RBI Layout, Bangalore 560078
              </p>
            </div>
            <div className="lc-glass-soft flex-1 rounded-card p-5 xl:p-7">
              <p className="label text-lc-mute">Prefer reading first?</p>
              <div className="mt-4 flex flex-wrap gap-2.5">
                <Link to="/about"><Chip tone="glass">Our mission</Chip></Link>
                <Link to="/lifecrafter-program"><Chip tone="glass">LifeCrafter Program</Chip></Link>
                <Link to="/communities"><Chip tone="glass">Communities</Chip></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* safety notes */}
      <section className="px-5 pb-28">
        <div className="container-lc">
          <p className="label text-lc-mute">A note on communication</p>
          <h2 className="mt-4 max-w-xl font-display text-4xl">Safe and trustworthy, always.</h2>
          <Accordion className="mt-8 max-w-3xl" items={SAFETY} />
        </div>
      </section>
    </PageShell>
  )
}
